
import React, { useEffect, useState } from 'react';
import { EnhancedDashboardCharts } from './EnhancedDashboardCharts';
import { ComprehensiveAuditSection } from './ComprehensiveAuditSection';
import { PerformanceMetrics } from './PerformanceMetrics';
import { RealTimeActivities } from './RealTimeActivities';
import { SmartInsights } from './SmartInsights';
import { CollaborativeAnalytics } from './CollaborativeAnalytics';
import { UnitConsistentCharts } from './UnitConsistentCharts';
import { MixedUnitAnalysisCharts } from './MixedUnitAnalysisCharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/Auth/AuthProvider';
import { ArrowRight, Target, TrendingUp, Users, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/hooks/useOrganization';

export const DashboardOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  // Determine if user is in an organization (corporate/enterprise)
  const [showCollaborative, setShowCollaborative] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      setLoadingProfile(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      setProfile(data);
      setLoadingProfile(false);
    };
    fetchProfile();
  }, [user]);

  const { isOrganization, loading: orgLoading } = useOrganization();

  useEffect(() => {
    // async function checkOrg() {
    //   if (!user) return;
    //   const { data, error } = await supabase
    //     .from('organization_members')
    //     .select('organization_id')
    //     .eq('user_id', user.id)
    //     .eq('status', 'active')
    //     .maybeSingle();
    //   setShowCollaborative(!!data?.organization_id);
    // }
    // checkOrg();
    setShowCollaborative(isOrganization);
  }, [user, isOrganization]);

  const hasValue = (value: unknown) =>
    typeof value === 'string' ? value.trim().length > 0 : Boolean(value);

  // Require at least 5 fields for both user types.
  // Individuals: personal details only.
  // Organizations: include core company information.
  const requiredProfileFields = isOrganization
    ? [
        profile?.full_name,
        profile?.job_title,
        profile?.company_name,
        profile?.company_website,
        profile?.industry_sector,
      ]
    : [
        profile?.full_name,
        profile?.job_title,
        profile?.department,
        profile?.location,
        profile?.phone,
      ];
  const requiredFieldsCount = requiredProfileFields.length;
  const completedFields = requiredProfileFields.filter(hasValue).length;
  const isProfileComplete = completedFields === requiredFieldsCount;
  const progress = requiredFieldsCount > 0
    ? (completedFields / requiredFieldsCount) * 100
    : 0;

  return (
    <div className="space-y-8" id="dashboard-metrics">
      {/* Profile Completion Progress Bar */}
      {!loadingProfile && !orgLoading && !isProfileComplete && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-4 mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-yellow-800">Complete your profile to unlock all features</span>
            </div>
            <Progress value={progress} className="h-2 bg-yellow-100" />
            <div className="text-xs text-yellow-700 mt-1">
              {completedFields} of {requiredFieldsCount} required fields completed
            </div>
          </div>
          <Button
            variant="outline"
            className="border-yellow-400 text-yellow-800 hover:bg-yellow-100"
            onClick={() => navigate('/settings?tab=profile')}
          >
            Complete Profile
          </Button>
        </div>
      )}
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.email?.split('@')[0] || 'Sustainability Manager'}! 👋
            </h1>
            <p className="text-gray-600 mt-2">
              Track your ESG progress and drive sustainable impact across your organization
            </p>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <Button
              className="flex items-center gap-2"
              onClick={() => navigate('/net-zero')}
            >
              <Target className="h-4 w-4" />
              Set New Goals
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Carbon Tracking
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-600">Monitor emissions across all scopes</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              Team Collaboration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-600">Manage sustainability teams</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Globe className="h-4 w-4 text-purple-600" />
              Global Standards
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-600">Align with international frameworks</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-orange-600" />
              Net-Zero Goals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-600">Track decarbonization progress</p>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <PerformanceMetrics />

      {/* Analytics Dashboard */}
      <EnhancedDashboardCharts />

      {/* Unit-Consistent Charts */}
      <UnitConsistentCharts />

      {/* Mixed Unit Analysis */}
      <MixedUnitAnalysisCharts />

      {/* Comprehensive Audit Section */}
      <ComprehensiveAuditSection />

      {/* Bottom Row - Activities and Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RealTimeActivities />
        <SmartInsights />
      </div>
      {showCollaborative && (
        <CollaborativeAnalytics />
      )}
    </div>
  );
};
